import React, { useState, useEffect, useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { AuthContext } from '../../../../../context/AuthContext';
import OrgTreeSelect from '../../../../../components/OrgSelect/OrgTreeSelect';
import { EditorContent } from '@tiptap/react';
import EditorToolbar from '../../../../../components/editor/EditorToolbar';
import tiptapExtensions from '../../../../../components/editor/tiptapExtensions';
import useEditorWithDragDrop from '../../../../../components/editor/useEditorWithDragDrop';
import FileAttachment from '../../../../../components/FileAttachment/FileAttachment';
import CommonDatePicker from '../../../../../components/DatePicker/CommonDatePicker';
import ModalSchedule from '../../../../../pages/schedule/ModalSchedule';
import '../../../../../pages/schedule/ModalSchedule.css';

function SafetyMeetingModal({ mode, initialData, onClose, onSave, onDelete, onStartEdit, canEdit }) {
  const { user } = useContext(AuthContext);

  const [formDate, setFormDate] = useState('');
  const [formTime, setFormTime] = useState('');
  const [formDuration, setFormDuration] = useState('');
  const [formLocation, setFormLocation] = useState('');
  const [assignees, setAssignees] = useState([]);
  const [formTitle, setFormTitle] = useState('');
  const [formAttendeeCnt, setFormAttendeeCnt] = useState('');
  const [formAgenda, setFormAgenda] = useState('1. 사업장 유해위험요인 파악 결과 심의 및 승인 건\n2. 고위험성(상/중 등급) 공정에 대한 안전보건 감소대책 실행계획 확정 건\n3. 기타 근로자 현장 건의사항 청취 및 개선 조치 방향 토의');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchUsers, setSearchUsers] = useState([]);
  const [showSearchPopup, setShowSearchPopup] = useState(false);

  const [formContent, setFormContent] = useState('');
  const [files, setFiles] = useState([]);
  const [editorHeight, setEditorHeight] = useState('400px');

  const [startDateTime, setStartDateTime] = useState(null);
  const [endDateTime, setEndDateTime] = useState(null);

  const [orgPopupOpen, setOrgPopupOpen] = useState(false);
  const [isAttendeeExpanded, setIsAttendeeExpanded] = useState(false);
  const [showExpandBtn, setShowExpandBtn] = useState(false);
  const attendeeListRef = useRef(null);

  useEffect(() => {
    if (attendeeListRef.current) {
      setShowExpandBtn(attendeeListRef.current.scrollHeight > 130);
    }
  }, [assignees, mode, isAttendeeExpanded, formAttendeeCnt]);

  // Editor hook
  const editor = useEditorWithDragDrop(tiptapExtensions);

  useEffect(() => {
    if (initialData) {
      setFormDate(initialData.date || '');
      setFormTime(initialData.time || '');
      setFormDuration(initialData.duration || '');
      setFormLocation(initialData.location || '');
      let newAssignees = [];
      // 1. 새 구조 (객체 배열)가 우선
      if (initialData.attendeesList && initialData.attendeesList.length > 0) {
        newAssignees = initialData.attendeesList.map((a, i) => ({
          emp_no: a.user_id || `manual_${i}`,
          name: a.user_name || '',
          position: a.user_position || '',
          department: a.dept_name || '',
          checked: false
        }));
        setAssignees(newAssignees);
      } else {
        // 2. 과거 레거시 텍스트 처리
        newAssignees = initialData.attendees
          ? initialData.attendees.split(',').map((s, i) => {
            if (!s.trim()) return null;
            // 예전 꼼수(|사번)도 혹시 남아있다면 파싱
            const parts = s.split('|');
            const namePart = parts[0].trim();
            const empNoPart = parts.length > 1 ? parts[1].trim() : `manual_${i}`;

            let parsedName = namePart, parsedPos = '', parsedDept = '';
            const deptMatch = namePart.match(/(.+?)\((.+?)\)$/);
            if (deptMatch) {
              parsedDept = deptMatch[2];
              const rest = deptMatch[1].trim().split(' ');
              parsedName = rest[0];
              if (rest.length > 1) parsedPos = rest.slice(1).join(' ');
            } else {
              const parts2 = namePart.split(' ');
              parsedName = parts2[0];
              if (parts2.length > 1) parsedPos = parts2.slice(1).join(' ');
            }
            return { emp_no: empNoPart, name: parsedName, position: parsedPos, department: parsedDept, checked: false };
          }).filter(Boolean)
          : [];
        setAssignees(newAssignees);
      }
      setFormAttendeeCnt(String(newAssignees.length) || initialData.attendeeCnt || '0');
      setFormTitle(initialData.title || '');
      setFormAttendeeCnt(initialData.attendeeCnt || '0');
      setFormAgenda(initialData.agenda || '1. 사업장 유해위험요인 파악 결과 심의 및 승인 건\n2. 고위험성(상/중 등급) 공정에 대한 안전보건 감소대책 실행계획 확정 건\n3. 기타 근로자 현장 건의사항 청취 및 개선 조치 방향 토의');
      setFormContent(initialData.content || '');
      setFiles(initialData.files || []);
      setEditorHeight('400px');

      if (initialData.date && initialData.time) {
        const dateParts = initialData.date.split(' ~ ').map(s => s.trim());
        const startDateStr = dateParts[0];
        const endDateStr = dateParts.length > 1 ? dateParts[1] : dateParts[0];

        const timeParts = initialData.time.split('~').map(s => s.trim());
        if (timeParts.length === 2) {
          const sd = new Date(`${startDateStr}T${timeParts[0]}:00`);
          const ed = new Date(`${endDateStr}T${timeParts[1]}:00`);
          if (!isNaN(sd.getTime())) setStartDateTime(sd);
          if (!isNaN(ed.getTime())) setEndDateTime(ed);
        }
      }

      if (editor && mode === 'WRITE') {
        editor.commands.setContent(initialData.content || '');
      }
    }
  }, [initialData, mode, editor]);

  const updateDateAndDuration = (start, end) => {
    if (!start && !end) return;

    let s = start || end;
    let e = end || start;

    if (e.getTime() < s.getTime()) {
      alert('종료 일시가 시작 일시보다 빠를 수 없습니다.');
      setEndDateTime(s);
      e = s;
    }

    const pad = (n) => String(n).padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const formatTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

    const startStr = formatDate(s);
    const endStr = formatDate(e);
    setFormDate(startStr === endStr ? startStr : `${startStr} ~ ${endStr}`);
    setFormTime(`${formatTime(s)} ~ ${formatTime(e)}`);

    // 오직 시/분 단위 차이만 계산 (날짜 무시)
    const startHour = s.getHours() + s.getMinutes() / 60;
    const endHour = e.getHours() + e.getMinutes() / 60;
    let diffHours = endHour - startHour;

    // 밤을 넘기는 시간대일 경우 (예: 23:00 ~ 01:00) 24시간을 더해 보정
    if (diffHours < 0) diffHours += 24;

    if (diffHours > 0) {
      const formatted = diffHours.toFixed(1);
      setFormDuration(formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted);
    } else {
      setFormDuration('0');
    }
  };

  const addAssignee = (u) => {
    setAssignees(prev => {
      if (prev.some(c => c.emp_no === u.userid)) return prev;
      const newList = [...prev, {
        emp_no: u.userid,
        name: u.name,
        position: u.position || '',
        department: u.dept_name || u.department || '',
        checked: false
      }];
      setFormAttendeeCnt(String(newList.length));
      return newList;
    });
  };

  const deleteAll = () => { setAssignees([]); setFormAttendeeCnt('0'); };

  const handleSearch = (term) => {
    const keyword = (term || '').trim();
    if (!keyword) { alert('검색대상이 입력되지 않았습니다.'); return; }
    const token = user?.token || sessionStorage.getItem('token');
    axios.get('/api/org/search', { params: { keyword }, headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const users = res.data.users || [];
        if (users.length === 0) { alert('검색 결과가 존재하지 않습니다.'); return; }
        users.forEach(u => addAssignee(u));
        setSearchTerm('');
      }).catch(err => console.error('검색 실패:', err));
  };

  const toggleSearchSelect = (item) => setSearchUsers(prev => prev.map(u => u.userid === item.userid ? { ...u, checked: !u.checked } : u));
  const handleSearchConfirm = () => { searchUsers.filter(u => u.checked).forEach(u => addAssignee(u)); setSearchUsers([]); setShowSearchPopup(false); setSearchTerm(''); };


  const handleSave = (e) => {
    e.preventDefault();
    const contentText = editor ? editor.getText().trim() : (formContent || '').replace(/<[^>]*>?/gm, '').trim();

    if (!formTitle.trim()) return alert('회의종류(제목)를 입력해 주세요.');
    if (!formDate.trim()) return alert('날짜(회의 일시)를 입력해 주세요.');
    if (!formLocation.trim()) return alert('회의장소를 입력해 주세요.');
    if (!formAttendeeCnt || Number(formAttendeeCnt) <= 0 || assignees.length === 0) return alert('참석자 명단을 1명 이상 등록해 주세요.');
    if (!contentText) return alert('회의내용을 입력해 주세요.');

    const docData = {
      title: formTitle,
      date: formDate,
      time: formTime,
      duration: formDuration,
      location: formLocation,
      // 하위호환용 텍스트 필드
      attendees: assignees.map(c => {
        if (String(c.emp_no).startsWith('manual_')) return c.name;
        const pos = c.position ? ` ${c.position}` : '';
        const dept = c.department ? `(${c.department})` : '';
        return `${c.name}${pos}${dept}`.trim();
      }).join(', '),
      // 신규 테이블 저장용 JSON 배열
      attendeesList: assignees.map(c => ({
        user_id: String(c.emp_no).startsWith('manual_') ? null : c.emp_no,
        user_name: c.name,
        user_position: c.position,
        dept_name: c.department
      })),
      attendeeCnt: formAttendeeCnt,
      agenda: formAgenda,
      content: editor ? editor.getHTML() : formContent,
      files: files
    };

    onSave(docData);
  };

  const handleOrgConfirm = (selectedTargets) => {
    setOrgPopupOpen(false);
    if (!selectedTargets || selectedTargets.length === 0) return;
    setAssignees(prev => {
      const manuals = prev.filter(a => String(a.emp_no).startsWith('manual_'));
      const formatted = selectedTargets.map(t => ({
        emp_no: t.target_id || t.user_id || t.id,
        name: t.target_name || t.name || '',
        position: t.position || '',
        department: t.dept_name || t.org_name || t.department || (t.org && t.org.dept_name) || '',
        checked: false
      }));
      const merged = [...manuals, ...formatted];
      setFormAttendeeCnt(String(merged.length));
      return merged;
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      const ss = String(d.getSeconds()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
    } catch {
      return dateStr;
    }
  };

  const isView = mode === 'DETAIL';

  return ReactDOM.createPortal(
    <div className="modal show d-block" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9998, overflow: 'hidden' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '1000px', width: '95%' }}>
        <div className="modal-content" style={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', overflow: 'hidden' }}>

          <div className="modal-header d-flex justify-content-between align-items-center" style={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', color: '#fff', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', padding: '16px 24px' }}>
            <h5 className="modal-title fw-bold m-0 fs-5">
              <i className={`fas ${isView ? 'fa-eye' : 'fa-edit'} me-2`}></i>
              {isView ? '위험성평가 결과 회의록(총평) 상세' : (initialData ? '회의록(총평) 수정' : '새 회의록(총평) 작성')}
            </h5>
            <div className="d-flex align-items-center gap-3">
              {isView && initialData && (
                <div className="d-flex align-items-center gap-3" style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.85)', paddingRight: '12px', borderRight: '1px solid rgba(255,255,255,0.3)', marginTop: '4px' }}>
                  <span><i className="fas fa-user-edit me-1"></i>{initialData.author || '-'}</span>
                  <span><i className="fas fa-clock me-1"></i>{formatDateTime(initialData.createdAt)}</span>
                </div>
              )}
              <button type="button" className="btn-close btn-close-white" onClick={onClose} style={{ boxShadow: 'none' }}></button>
            </div>
          </div>

          <div className="modal-body" style={{ padding: '20px 30px', height: '65vh', minHeight: '450px', overflowY: 'auto', backgroundColor: '#f8fafc' }}>
            <div className="row g-3">
              {/* 회의종류 (제목) */}
              <div className="col-12">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-clipboard-list me-1 text-primary"></i> 회의종류 (제목) {!isView && <span className="text-danger">*</span>}
                  </label>
                  {isView ? (
                    <div className="text-dark px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9' }}>{formTitle || '-'}</div>
                  ) : (
                    <input type="text" className="form-control" value={formTitle} onChange={e => setFormTitle(e.target.value)} placeholder="예: 위험성평가 실시 결과 총평 회의" style={{ fontSize: '0.825rem' }} />
                  )}
                </div>
              </div>

              {/* 회의 일시 및 시간 */}
              <div className="col-12">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-calendar-alt me-1 text-primary"></i> 회의 일시 및 시간 {!isView && <span className="text-danger">*</span>}
                  </label>
                  {isView ? (
                    <div className="d-flex flex-wrap gap-3">
                      <div className="text-dark px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9' }}>
                        <span className="text-secondary me-2">날짜:</span>{formDate || '-'}
                      </div>
                      <div className="text-dark px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9' }}>
                        <span className="text-secondary me-2">시간:</span>{formTime || '-'}
                      </div>
                      <div className="text-dark px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9' }}>
                        <span className="text-secondary me-2">소요시간:</span>{formDuration || 0}시간
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center gap-3 flex-wrap date-picker-pointer">
                      <style>{`
                        .date-picker-pointer input {
                          cursor: pointer !important;
                        }
                        .modal-body .form-control::placeholder {
                          color: #adb5bd !important;
                          opacity: 0.7 !important;
                          font-weight: 400 !important;
                        }
                      `}</style>

                      {/* 날짜 직접 입력란 + 시작일/종료일 달력 */}
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-semibold text-secondary" style={{ fontSize: '0.825rem' }}>날짜:</span>
                        <input type="text" className="form-control" value={formDate} onChange={e => setFormDate(e.target.value)} placeholder="예: 2026-08-11 또는 2026-08-11 ~ 2026-08-12" style={{ width: '310px', height: '34px', fontSize: '0.825rem' }} />

                        <div style={{ position: 'relative', zIndex: 1050, marginLeft: '4px' }}>
                          <CommonDatePicker
                            value={startDateTime}
                            onChange={date => {
                              setStartDateTime(date);
                              updateDateAndDuration(date, endDateTime);
                            }}
                            showTime={true}
                            useButton={true}
                            buttonLabel={<i className="fas fa-calendar-alt" title="시작 일시 선택"></i>}
                          />
                        </div>
                        <span className="text-secondary fw-bold">~</span>
                        <div style={{ position: 'relative', zIndex: 1050 }}>
                          <CommonDatePicker
                            value={endDateTime}
                            onChange={date => {
                              setEndDateTime(date);
                              updateDateAndDuration(startDateTime, date);
                            }}
                            showTime={true}
                            useButton={true}
                            buttonLabel={<i className="fas fa-calendar-check" title="종료 일시 선택"></i>}
                            minDate={startDateTime}
                            minTime={startDateTime && endDateTime && startDateTime.toDateString() === endDateTime.toDateString() ? startDateTime : undefined}
                          />
                        </div>
                      </div>

                      {/* 시간 직접 입력란 */}
                      <div className="d-flex align-items-center gap-2 border-start ps-3" style={{ borderColor: '#e2e8f0' }}>
                        <span className="fw-semibold text-secondary" style={{ fontSize: '0.825rem' }}>시간:</span>
                        <input type="text" className="form-control" value={formTime} onChange={e => setFormTime(e.target.value)} placeholder="예: 14:30~15:30" style={{ width: '130px', height: '34px', fontSize: '0.825rem' }} />
                      </div>

                      {/* 소요시간 직접 입력란 */}
                      <div className="d-flex align-items-center gap-2 border-start ps-3" style={{ borderColor: '#e2e8f0' }}>
                        <span className="fw-semibold text-secondary" style={{ fontSize: '0.825rem' }}>소요시간:</span>
                        <input type="text" className="form-control text-center" value={formDuration} onChange={e => setFormDuration(e.target.value)} placeholder="예: 1" style={{ width: '60px', height: '34px', fontSize: '0.825rem' }} />
                        <span style={{ fontSize: '0.825rem' }}>시간</span>
                      </div>

                    </div>
                  )}
                </div>
              </div>

              {/* 회의장소 */}
              <div className="col-md-6">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-map-marker-alt me-1 text-primary"></i> 회의장소 {!isView && <span className="text-danger">*</span>}
                  </label>
                  {isView ? (
                    <div className="text-dark px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9' }}>{formLocation || '-'}</div>
                  ) : (
                    <input type="text" className="form-control" value={formLocation} onChange={e => setFormLocation(e.target.value)} placeholder="예: 3층 회의실" style={{ fontSize: '0.825rem' }} />
                  )}
                </div>
              </div>

              {/* 참석 인원 */}
              <div className="col-md-6">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-users me-1 text-primary"></i> 참석 인원 {!isView && <span className="text-danger">*</span>}
                  </label>
                  {isView ? (
                    <div className="text-dark px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9' }}>{formAttendeeCnt || '0'} 명</div>
                  ) : (
                    <div className="d-flex align-items-center gap-2">
                      <input type="number" className="form-control text-center bg-light" value={formAttendeeCnt} readOnly style={{ width: '100px', height: '34px', fontSize: '0.825rem', cursor: 'not-allowed', color: '#6c757d' }} />
                      <span style={{ fontSize: '0.825rem' }}>명</span>
                    </div>
                  )}
                </div>
              </div>

              {/* 참석자 명단 */}
              <div className="col-12">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                      <i className="fas fa-users me-1 text-primary"></i> 참석자 명단 {!isView && <span className="text-danger">*</span>}
                      {assignees.length > 0 && <span className="ms-2 fw-semibold" style={{ fontSize: '0.75rem', color: '#64748b' }}>(총 {assignees.length}명)</span>}
                    </label>
                    {showExpandBtn && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        style={{ padding: '0px 6px', fontSize: '12px' }}
                        onClick={() => setIsAttendeeExpanded(!isAttendeeExpanded)}
                        title={isAttendeeExpanded ? "접기" : "펼쳐보기"}
                      >
                        <i className={`fas ${isAttendeeExpanded ? 'fa-minus' : 'fa-plus'}`}></i>
                      </button>
                    )}
                  </div>
                  <div className="card shadow-sm border-0 bg-light bg-opacity-50 mb-0">
                    <div className="card-body p-2">
                      {isView ? (
                        <div ref={attendeeListRef} className="d-flex flex-wrap gap-2 align-items-center bg-white p-2 border rounded" style={{ maxHeight: isAttendeeExpanded ? 'none' : '130px', overflowY: 'auto', transition: 'max-height 0.3s ease' }}>
                          {assignees.length > 0 ? assignees.map((a, i) => (
                            <span key={i} className="badge" style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: 600, fontSize: '13px', padding: '5px 10px' }}>
                              {a.name}{a.position ? ` ${a.position}` : ''}{a.department ? ` (${a.department})` : ''}
                            </span>
                          )) : <span className="text-muted" style={{ fontSize: '13px' }}>-</span>}
                        </div>
                      ) : (
                        <>
                          <div className="form-schedule-search-bar mb-2 d-flex align-items-center w-100">
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (searchTerm.trim()) handleSearch(searchTerm); } }}
                                placeholder="이름 검색"
                                className="form-control d-inline-block w-auto"
                                style={{ minWidth: '200px', fontSize: '13px', height: '32px' }}
                              />
                              <button type="button" className="btn btn-sm" style={{ backgroundColor: '#343a40', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }} onClick={() => handleSearch(searchTerm)}>검색</button>
                              <button type="button" className="btn btn-sm" style={{ backgroundColor: 'transparent', border: '1px solid #007bff', color: '#007bff', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }} onClick={() => setOrgPopupOpen(true)}>조직도 선택</button>
                            </div>
                            {assignees.length > 0 && (
                              <button type="button" className="btn btn-sm text-nowrap ms-auto" style={{ backgroundColor: '#fff', border: '1px solid #dc3545', color: '#dc3545', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }} onClick={deleteAll}>
                                <i className="fas fa-trash-alt me-1"></i>전체삭제
                              </button>
                            )}
                          </div>
                          {assignees.length > 0 ? (
                            <div ref={attendeeListRef} className="d-flex flex-wrap gap-2 border rounded p-3 bg-white" style={{ maxHeight: isAttendeeExpanded ? 'none' : '130px', overflowY: 'auto', transition: 'max-height 0.3s ease' }}>
                              {assignees.map((a, i) => (
                                <span key={a.emp_no} className="badge d-flex align-items-center gap-2" style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: 600, fontSize: '13px', padding: '5px 10px' }}>
                                  <span>{a.name}{a.position ? ` ${a.position}` : ''}{a.department ? ` (${a.department})` : ''}</span>
                                  <i className="fas fa-times-circle text-primary" style={{ cursor: 'pointer' }} onClick={() => {
                                    setAssignees(prev => {
                                      const res = prev.filter(item => item.emp_no !== a.emp_no);
                                      setFormAttendeeCnt(String(res.length));
                                      return res;
                                    });
                                  }}></i>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 border rounded bg-white text-muted" style={{ fontSize: '0.8rem' }}>
                              참석자가 지정되지 않았습니다. 이름 검색 또는 조직도 버튼을 눌러 추가해 주세요.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* 회의 안건 */}
              <div className="col-12">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-list-ul me-1 text-primary"></i> 회의 안건 (자유 양식)
                  </label>
                  {isView ? (
                    <div className="text-dark px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9', whiteSpace: 'pre-wrap' }}>
                      {formAgenda || '-'}
                    </div>
                  ) : (
                    <textarea
                      className="form-control"
                      value={formAgenda}
                      onChange={e => setFormAgenda(e.target.value)}
                      rows={4}
                      placeholder="예: 1. 유해위험요인 파악 결과 심의..."
                      style={{ fontSize: '0.825rem', resize: 'none' }}
                    />
                  )}
                </div>
              </div>              {/* 회의내용 */}
              <div className="col-12">
                <div className="p-0 bg-white d-flex flex-column mt-3" style={{ height: editorHeight, transition: 'height 0.3s ease' }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                      <i className="fas fa-file-alt me-1 text-primary"></i> 회의내용 {!isView && <span className="text-danger">*</span>}
                    </label>
                    {!isView && (
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-secondary fw-semibold" style={{ fontSize: '0.8rem' }}>에디터 높이:</span>
                        <select
                          className="form-select form-select-sm text-secondary"
                          style={{ width: '130px', fontSize: '0.8rem', cursor: 'pointer' }}
                          value={editorHeight}
                          onChange={(e) => setEditorHeight(e.target.value)}
                        >
                          <option value="400px">기본 (400px)</option>
                          <option value="600px">넓게 (600px)</option>
                          <option value="800px">아주 넓게 (800px)</option>
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="flex-grow-1 bg-light rounded-2 p-1" style={{ minHeight: 0 }}>
                    {isView ? (
                      <div className="post-content bg-white p-3 rounded shadow-sm h-100 overflow-auto">
                        <style>{`
                          .post-content p { margin-top: 0; margin-bottom: 0.2em; font-size: 10pt; }
                          .post-content { font-size: 10pt; line-height: 1.2; }
                        `}</style>
                        <div dangerouslySetInnerHTML={{ __html: formContent }} />
                      </div>
                    ) : (
                      <div className="border p-1 rounded bg-white h-100 d-flex flex-column">
                        {editor && <EditorToolbar editor={editor} />}
                        <div className="flex-grow-1 overflow-auto bg-white" style={{ minHeight: 0 }}>
                          <style>{`
                            .ProseMirror p { margin-top: 0; margin-bottom: 0.2em; font-size: 10pt; }
                            .ProseMirror { font-size: 10pt; line-height: 1.2; }
                          `}</style>
                          <EditorContent editor={editor} className="editor-content h-100" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 비고 (첨부파일) */}
              {(!isView || (files && files.filter(f => !f.isDeleted).length > 0)) && (
                <div className="col-12">
                  <div className="p-0 bg-white d-flex flex-column mt-4">
                    <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                      <i className="fas fa-paperclip me-1 text-primary"></i> 비고 (첨부파일)
                    </label>
                    <div className="flex-grow-1 overflow-auto bg-light rounded-2 p-1" style={{ minHeight: 0 }}>
                      <FileAttachment files={files} setFiles={setFiles} mode={isView ? 'view' : 'edit'} />
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>

          <div className="modal-footer" style={{ borderTop: '1px solid #cbd5e1', backgroundColor: '#f1f5f9', padding: '12px 24px', display: 'flex', justifyContent: 'flex-end' }}>
            <div className="d-flex" style={{ gap: '8px' }}>
              {isView ? (
                <>
                  {canEdit && <button type="button" className="btn btn-primary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={onStartEdit}>수정</button>}
                  {canEdit && <button type="button" className="btn btn-danger btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={onDelete}>삭제</button>}
                  <button type="button" className="btn btn-secondary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={onClose}>닫기</button>
                </>
              ) : (
                <>
                  <button type="button" className="btn btn-primary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={handleSave}>저장</button>
                  <button type="button" className="btn btn-secondary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={onClose}>취소</button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 검색 결과 모달 */}
      {showSearchPopup && (
        <ModalSchedule
          type="searchResult"
          data={{ users: searchUsers, depts: [] }}
          toggleSelect={toggleSearchSelect}
          onConfirm={handleSearchConfirm}
          onClose={() => { setShowSearchPopup(false); setSearchUsers([]); }}
        />
      )}

      {/* 조직도 팝업 */}
      {orgPopupOpen && (
        <OrgTreeSelect
          selectionMode="multi"
          targetType="user"
          initialSelected={assignees.map(w => ({
            target_type: 'user',
            target_id: String(w.emp_no),
            target_name: w.name,
            position: w.position,
            dept_name: w.department,
            disp_target_name: `${w.name}${w.position ? " / " + w.position : ""}${w.department ? " (" + w.department + ")" : ""}`
          }))}
          onConfirm={handleOrgConfirm}
          onCancel={() => setOrgPopupOpen(false)}
        />
      )}
    </div>,
    document.body
  );
}

export default SafetyMeetingModal;
